import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

interface UseProfileCompletionReturn {
  needsCompletion: boolean;
  loading: boolean;
  error: string | null;
  completeProfile: () => Promise<boolean>;
  resetProfile: () => Promise<boolean>;
}

/**
 * Hook to check and manage profile completion status
 * Used to determine if the profile completion modal should be shown
 */
export const useProfileCompletion = (): UseProfileCompletionReturn => {
  const [needsCompletion, setNeedsCompletion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if profile is complete
  const checkProfileCompletion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authenticated user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setNeedsCompletion(false);
        return;
      }

      // Skip for counselors - they don't have student profiles
      const userRole = user.user_metadata?.role;
      if (userRole === "counselor") {
        setNeedsCompletion(false);
        setLoading(false);
        return;
      }

      // Fetch profile_completed status from students table
      const { data, error: fetchError } = await supabase
        .from("students")
        .select("profile_completed")
        .eq("auth_user_id", user.id)
        .single();

      if (fetchError) {
        console.error("Error fetching profile completion status:", fetchError);
        setError("Unable to check profile status");
        setNeedsCompletion(false);
        return;
      }

      // Set needsCompletion to true only if profile is NOT completed in DB
      setNeedsCompletion(!data.profile_completed);
    } catch (err) {
      console.error("Profile completion check error:", err);
      setError("Unable to check profile status");
      setNeedsCompletion(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark profile as complete
  const completeProfile = useCallback(async (): Promise<boolean> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { error: updateError } = await supabase
        .from("students")
        .update({ profile_completed: true })
        .eq("auth_user_id", user.id);

      if (updateError) {
        console.error("Error completing profile:", updateError);
        return false;
      }

      setNeedsCompletion(false);
      return true;
    } catch (err) {
      console.error("Complete profile error:", err);
      return false;
    }
  }, []);

  // Reset profile completion status (for development/testing only)
  const resetProfile = useCallback(async (): Promise<boolean> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { error: updateError } = await supabase
        .from("students")
        .update({ profile_completed: false })
        .eq("auth_user_id", user.id);

      if (updateError) {
        console.error("Error resetting profile:", updateError);
        return false;
      }

      setNeedsCompletion(true);
      return true;
    } catch (err) {
      console.error("Reset profile error:", err);
      return false;
    }
  }, []);

  // Check on mount
  useEffect(() => {
    checkProfileCompletion();
  }, [checkProfileCompletion]);

  return {
    needsCompletion,
    loading,
    error,
    completeProfile,
    resetProfile,
  };
};
